import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Momentos canónicos (unificados 2026-02) — coinciden 1:1 con recipe_library.meal_slot
const MEAL_SLOTS = ['Desayuno', 'Snack AM', 'Almuerzo', 'Snack PM', 'Cena', 'Post-Entreno'];
// Tipos de dieta canónicos
const DIET_TYPES = [
  'Equilibrada',
  'Alta en Proteína',
  'Baja en Carbohidratos',
  'Alta en Carbohidratos',
  'Keto',
  'Vegetariana',
  'Vegana',
  'Sin Gluten',
  'Sin Lácteos',
];

const emptyRecipe = () => ({
  name: '', meal_slot: 'Almuerzo', diet_type: 'Equilibrada', prep_minutes: 15,
  calories: 400, protein: 30, carbs: 40, fats: 15,
  ingredients: [{ name: '', quantity: '', unit: 'g' }],
  instructions: [''],
  image_url: '', allergens: '', tags: '', uses_protein_powder: false,
  notes: '', active: true,
});

export default function RecipeLibraryManager() {
  const [tab, setTab] = useState('recipes');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterSlot, setFilterSlot] = useState('');
  const [filterDietType, setFilterDietType] = useState(''); // '' = Todas las dietas
  const [filterSearch, setFilterSearch] = useState('');
  // Nuevos filtros (2026-02)
  const [filterNoImage, setFilterNoImage] = useState(false);
  const [filterGlutenFree, setFilterGlutenFree] = useState(false);
  const [filterDairyFree, setFilterDairyFree] = useState(false);
  const [filterAllergens, setFilterAllergens] = useState([]);  // slugs
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const [autoTagging, setAutoTagging] = useState(false);
  const [editing, setEditing] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(null); // receta para vista previa
  const [generatingLLM, setGeneratingLLM] = useState(false);
  const [llmDialogOpen, setLlmDialogOpen] = useState(false);
  const [llmForm, setLlmForm] = useState({ meal_slot: 'Almuerzo', diet_type: 'Equilibrada', target_calories: 450, count: 3 });
  const [classifying, setClassifying] = useState(false);
  const [normalizingSlots, setNormalizingSlots] = useState(false);
  const [normalizeResult, setNormalizeResult] = useState(null); // resumen del endpoint
  // 2026-02 · Repoblar image_url para recetas legacy que quedaron sólo con
  // image_base64 (cuando la subida original a Cloudinary falló).
  const [repopulatingImages, setRepopulatingImages] = useState(false);
  // 2026-02 · Eliminar recetas incompletas (sin gramaje o macros en 0)
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  const [incompleteLoading, setIncompleteLoading] = useState(false);
  const [incompleteItems, setIncompleteItems] = useState([]);
  const [incompleteSelected, setIncompleteSelected] = useState(new Set());
  const [incompleteDeleting, setIncompleteDeleting] = useState(false);

  const loadIncompleteRecipes = async () => {
    setIncompleteLoading(true);
    try {
      const r = await axios.get(`${API}/admin/recipe-library/incomplete`);
      const list = r.data?.items || [];
      setIncompleteItems(list);
      setIncompleteSelected(new Set());
      setIncompleteOpen(true);
      if (list.length === 0) {
        toast.success('¡No hay recetas incompletas! Todas tienen gramajes y macros.');
      }
    } catch (e) {
      toast.error('Error cargando recetas incompletas: ' + (e.response?.data?.detail || e.message));
    } finally {
      setIncompleteLoading(false);
    }
  };

  const toggleIncompleteOne = (id) => {
    setIncompleteSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleIncompleteAll = () => {
    setIncompleteSelected(prev => {
      // Si ya están todas seleccionadas → deseleccionar todas
      if (prev.size === incompleteItems.length) return new Set();
      return new Set(incompleteItems.map(r => r.id));
    });
  };

  const confirmDeleteIncomplete = async () => {
    const ids = Array.from(incompleteSelected);
    if (ids.length === 0) {
      toast.error('Selecciona al menos una receta antes de eliminar');
      return;
    }
    if (!window.confirm(`⚠️ Vas a ELIMINAR ${ids.length} receta(s) incompleta(s) de la biblioteca. Esta acción es irreversible. ¿Continuar?`)) return;
    setIncompleteDeleting(true);
    try {
      const r = await axios.post(`${API}/admin/recipe-library/bulk-delete`, { ids });
      const d = r.data || {};
      toast.success(`🗑️ Eliminadas ${d.deleted || 0} de ${d.requested || ids.length} recetas`, { duration: 7000 });
      setIncompleteOpen(false);
      setIncompleteItems([]);
      setIncompleteSelected(new Set());
      fetchAll();
    } catch (e) {
      toast.error('Error al eliminar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setIncompleteDeleting(false);
    }
  };

  const handleRepopulateImages = async () => {
    if (!window.confirm('Se re-subirán a Cloudinary las imágenes de las recetas que quedaron sólo con base64 (sin image_url). Puede tardar 1-2 min si hay muchas. ¿Continuar?')) return;
    setRepopulatingImages(true);
    const t = toast.loading('🖼️ Re-subiendo imágenes a Cloudinary…');
    try {
      const r = await axios.post(`${API}/admin/recipe-library/repopulate-image-urls`, {}, { timeout: 300000 });
      const d = r.data || {};
      toast.success(
        `✅ Repoblación lista · ${d.uploaded || 0}/${d.total || 0} imágenes re-subidas · ${d.failed || 0} fallaron · ${d.skipped || 0} sin base64`,
        { id: t, duration: 9000 }
      );
      if ((d.errors_sample || []).length) {
        console.warn('[repopulate-images] errores:', d.errors_sample);
      }
      fetchAll();
    } catch (e) {
      toast.error('Error repoblando imágenes: ' + (e.response?.data?.detail || e.message), { id: t });
    } finally { setRepopulatingImages(false); }
  };

  const handleNormalizeMealSlots = async () => {
    if (!window.confirm('Se normalizará el campo meal_slot en TODA la biblioteca a los 6 valores canónicos (Desayuno, Snack AM, Almuerzo, Snack PM, Cena, Post-Entreno). Idempotente: podés correrlo de nuevo sin riesgo. ¿Continuar?')) return;
    setNormalizingSlots(true);
    try {
      const r = await axios.post(`${API}/admin/recipe-library/normalize-meal-slots`);
      setNormalizeResult(r.data);
      toast.success(`✅ Normalización lista · ${r.data.updated} corregidos · ${r.data.unchanged} sin cambios`, { duration: 8000 });
      fetchAll();
    } catch (e) {
      toast.error('Error al normalizar: ' + (e.response?.data?.detail || e.message));
    } finally { setNormalizingSlots(false); }
  };

  const handleAutoClassify = async () => {
    setClassifying(true);
    try {
      // 1. Vista previa (dry_run)
      const preview = await axios.post(`${API}/admin/recipe-library/auto-classify?dry_run=true`);
      const p = preview.data;
      if (!p.total_changes) {
        toast.info('¡Nada que reclasificar! Todas las recetas ya están bien categorizadas.');
        return;
      }
      // 2. Confirmación con resumen
      const summary = `Se reclasificarán ${p.total_changes} recetas de ${p.total_scanned}:\n\n`
        + `🍳 Desayuno: ${p.counts_after?.desayuno || 0}\n`
        + `🍴 Cena: ${p.counts_after?.cena || 0}\n`
        + `🥨 Snack: ${p.counts_after?.snack || 0}\n\n`
        + `¿Aplicar los cambios? (Sólo afecta las que están como "almuerzo")`;
      if (!window.confirm(summary)) return;
      // 3. Aplicar
      const applied = await axios.post(`${API}/admin/recipe-library/auto-classify?dry_run=false`);
      toast.success(`✅ ${applied.data.applied} recetas reclasificadas correctamente`);
      fetchAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setClassifying(false); }
  };

  const handleGenerateLLM = async () => {
    setGeneratingLLM(true);
    try {
      const r = await axios.post(`${API}/admin/recipe-library/generate-with-llm`, llmForm);
      toast.success(`Generadas ${r.data.generated} · Guardadas ${r.data.saved_to_library} en biblioteca`);
      setLlmDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error('Error generando: ' + (e.response?.data?.detail || e.message));
    } finally { setGeneratingLLM(false); }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSlot) params.set('meal_slot', filterSlot);
      if (filterDietType) params.set('diet_type', filterDietType);
      if (filterSearch) params.set('search', filterSearch);
      if (filterNoImage) params.set('no_image', 'true');
      if (filterGlutenFree) params.set('is_gluten_free', 'true');
      if (filterDairyFree) params.set('is_dairy_free', 'true');
      if (filterAllergens.length > 0) params.set('exclude_allergens', filterAllergens.join(','));
      params.set('skip', String(page * PAGE_SIZE));
      params.set('limit', String(PAGE_SIZE));
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/admin/recipe-library?${params.toString()}`),
        axios.get(`${API}/admin/recipe-library-stats`),
        axios.get(`${API}/admin/recipe-library-settings`),
      ]);
      setItems(r1.data.items || []);
      setTotal(r1.data.total || 0);
      setStats(r2.data);
      setSettings(r3.data);
    } catch (e) {
      toast.error('Error cargando biblioteca: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filterSlot, filterDietType, filterNoImage, filterGlutenFree, filterDairyFree, filterAllergens, page]);
  // Reset page al cambiar filtros (búsqueda no dispara auto — se resetea al aceptar)
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [filterSlot, filterDietType, filterNoImage, filterGlutenFree, filterDairyFree, filterAllergens]);

  const runAutoTag = async () => {
    if (!window.confirm('Se recorrerá toda la biblioteca de recetas para detectar automáticamente los alérgenos (Frutos Secos, Huevo, Soya, Lácteos, Gluten, etc.) y las categorías de dieta (Vegetariana, Vegana, Sin Gluten, Sin Lácteos) a partir de los ingredientes. Las categorías manuales como Keto o Alta Proteína se preservan. ¿Continuar?')) return;
    setAutoTagging(true);
    try {
      const r = await axios.post(`${API}/admin/recipe-library/auto-tag`);
      const dt = r.data.by_diet_type || {};
      const parts = [
        `Etiquetado listo: ${r.data.updated}/${r.data.total} recetas`,
        `${r.data.gluten_free} sin gluten · ${r.data.dairy_free} sin lácteos`,
        `${dt['Vegetariana'] || 0} vegetariana · ${dt['Vegana'] || 0} vegana`,
      ];
      toast.success(parts.join(' · '));
      fetchAll();
    } catch (e) {
      toast.error('Error auto-tag: ' + (e.response?.data?.detail || e.message));
    } finally { setAutoTagging(false); }
  };

  const quickUploadImage = async (recipe, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen supera 5MB. Redúcela antes de subir.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('recipe_id', recipe.id);
    try {
      const r = await axios.post(`${API}/admin/recipe-library/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const storage = r.data?.storage === 'cloudinary' ? '☁️ Cloudinary' : '💾 base64';
      toast.success(`Imagen subida a "${recipe.name}" (${storage})`);
      fetchAll();
    } catch (e) {
      toast.error('Error subiendo imagen: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSaveRecipe = async () => {
    if (!editing?.name) { toast.error('El nombre es obligatorio'); return; }
    const payload = {
      ...editing,
      allergens: typeof editing.allergens === 'string' ? editing.allergens.split(',').map(s => s.trim()).filter(Boolean) : (editing.allergens || []),
      tags: typeof editing.tags === 'string' ? editing.tags.split(',').map(s => s.trim()).filter(Boolean) : (editing.tags || []),
    };
    // ─── PROTECCIÓN DE IMAGEN (2026-02) ─────────────────────────────
    // Al editar, si el admin no cambió la imagen, NO enviamos los campos
    // vacíos — así el backend preserva la URL de Cloudinary existente.
    // Solo enviamos image_url/image_base64 si están definidos y no son vacíos.
    if (!payload.image_url) delete payload.image_url;
    if (!payload.image_base64) delete payload.image_base64;
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/recipe-library/${editing.id}`, payload);
        toast.success('Receta actualizada');
      } else {
        await axios.post(`${API}/admin/recipe-library`, payload);
        toast.success('Receta creada');
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error('Error guardando: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta receta de la biblioteca?')) return;
    try {
      await axios.delete(`${API}/admin/recipe-library/${id}`);
      toast.success('Receta eliminada');
      fetchAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm('¿Migrar todas las recetas ya generadas a la biblioteca? Esto es un proceso único que reutiliza lo que ya pagaste.')) return;
    setMigrating(true);
    try {
      const r = await axios.post(`${API}/admin/recipe-library/migrate`);
      toast.success(`Migradas: ${r.data.migrated} | Duplicadas: ${r.data.skipped_duplicates} | Errores: ${r.data.errors}`);
      fetchAll();
    } catch (e) {
      toast.error('Error en migración: ' + (e.response?.data?.detail || e.message));
    } finally { setMigrating(false); }
  };

  const handleUpdateSettings = async (key, value) => {
    try {
      const r = await axios.patch(`${API}/admin/recipe-library-settings`, { [key]: value });
      setSettings(r.data);
      toast.success('Ajuste guardado');
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ── Upload de foto desde el computador ──
  const handleUploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen supera 5MB. Redúcela antes de subir.');
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/recipe-library/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Unificado (2026-02): usamos image_url como fuente única. Cloudinary
      // devuelve la URL permanente; si falla, cae al base64 heredado.
      if (r.data?.image_url) {
        setEditing(prev => ({ ...prev, image_url: r.data.image_url, image_base64: '' }));
      } else if (r.data?.image_base64) {
        setEditing(prev => ({ ...prev, image_base64: r.data.image_base64, image_url: '' }));
      }
      toast.success(r.data?.storage === 'cloudinary' ? 'Imagen guardada en Cloudinary' : 'Imagen cargada');
    } catch (e) {
      toast.error('Error subiendo imagen: ' + (e.response?.data?.detail || e.message));
    } finally { setUploadingImage(false); }
  };

  // ── Import CSV / Excel ──
  const parseCsv = (text) => {
    // Parser CSV que respeta campos entrecomillados con comas internas.
    // Soporta:
    //   - Comillas dobles escapadas ("" dentro de un campo quoted)
    //   - Saltos de línea (los quotes multi-línea NO se soportan; asumimos 1 fila = 1 línea)
    //   - Campos sin comillas (split por coma normal)
    const parseLine = (line) => {
      const cells = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"' && line[i + 1] === '"') {
            // Comilla escapada
            cur += '"';
            i++;
          } else if (ch === '"') {
            inQuotes = false;
          } else {
            cur += ch;
          }
        } else {
          if (ch === '"') {
            inQuotes = true;
          } else if (ch === ',') {
            cells.push(cur);
            cur = '';
          } else {
            cur += ch;
          }
        }
      }
      cells.push(cur);
      return cells.map((c) => c.trim());
    };

    const rows = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (rows.length < 2) return [];
    const headers = parseLine(rows[0]).map((h) => h.trim().toLowerCase());
    return rows.slice(1).map((line) => {
      const cells = parseLine(line);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cells[i] ?? ''; });
      // Convertir números
      ['calories', 'protein', 'carbs', 'fats', 'prep_minutes'].forEach((k) => {
        if (obj[k] !== undefined && obj[k] !== '') obj[k] = parseFloat(obj[k]) || 0;
      });
      // Parsear el JSON de ingredients (viene como celda entrecomillada con JSON dentro)
      if (obj.ingredients && typeof obj.ingredients === 'string' && obj.ingredients.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(obj.ingredients);
          if (Array.isArray(parsed)) {
            obj.ingredients = parsed.map((ing) => ({
              name: ing.name || '',
              quantity: ing.quantity ?? '',
              unit: ing.unit || 'g',
            }));
          }
        } catch (e) {
          console.warn('No se pudo parsear ingredients JSON de la fila:', obj.name, e.message);
        }
      }
      return obj;
    });
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) {
        toast.error('El archivo está vacío o no tiene el formato correcto.');
        return;
      }
      const r = await axios.post(`${API}/admin/recipe-library/import-csv`, { rows });
      toast.success(`Importadas: ${r.data.imported} de ${rows.length} filas`);
      if (r.data.errors?.length) {
        console.warn('Filas con error:', r.data.errors);
      }
      fetchAll();
    } catch (e) {
      toast.error('Error importando: ' + (e.response?.data?.detail || e.message));
    } finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const csv = [
      'name,meal_slot,diet_type,prep_minutes,calories,protein,carbs,fats,ingredients,instructions,image_url,allergens,tags,uses_protein_powder,notes',
      'Bowl de pollo con quinoa,almuerzo,equilibrada,15,480,35,45,15,"[{""name"":""Pollo"",""quantity"":120,""unit"":""g""},{""name"":""Quinoa"",""quantity"":80,""unit"":""g""}]",Cocinar el pollo | Servir con quinoa,,gluten,alto_proteico,false,Ejemplo',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_biblioteca_recetas.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEdit = async (recipe) => {
    if (!recipe) { setEditing(emptyRecipe()); return; }
    // Cargar receta completa con image_base64
    try {
      const r = await axios.get(`${API}/admin/recipe-library/${recipe.id}`);
      const full = r.data;
      setEditing({ ...full, allergens: (full.allergens || []).join(', '), tags: (full.tags || []).join(', ') });
    } catch {
      setEditing({ ...recipe, allergens: (recipe.allergens || []).join(', '), tags: (recipe.tags || []).join(', ') });
    }
  };

  const openPreview = async (recipe) => {
    try {
      const r = await axios.get(`${API}/admin/recipe-library/${recipe.id}`);
      setPreviewing(r.data);
    } catch {
      setPreviewing(recipe);
    }
  };

  const updateEditingField = (key, val) => setEditing(prev => ({ ...prev, [key]: val }));

  const updateIngredient = (idx, key, val) => {
    setEditing(prev => {
      const list = [...(prev.ingredients || [])];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, ingredients: list };
    });
  };

  const addIngredient = () => setEditing(prev => ({ ...prev, ingredients: [...(prev.ingredients || []), { name: '', quantity: '', unit: 'g' }] }));
  const removeIngredient = (idx) => setEditing(prev => ({ ...prev, ingredients: (prev.ingredients || []).filter((_, i) => i !== idx) }));

  const updateInstruction = (idx, val) => {
    setEditing(prev => {
      const list = [...(prev.instructions || [])];
      list[idx] = val;
      return { ...prev, instructions: list };
    });
  };
  const addInstruction = () => setEditing(prev => ({ ...prev, instructions: [...(prev.instructions || []), ''] }));
  const removeInstruction = (idx) => setEditing(prev => ({ ...prev, instructions: (prev.instructions || []).filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-4" data-testid="recipe-library-manager">
      {/* Header + stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <StatCard label="Total activas" value={stats?.total_active ?? '—'} color="#22d3ee" />
        <StatCard label="Con imagen" value={stats?.with_image ?? '—'} color="#facc15" />
        <StatCard label="Manuales" value={stats?.by_source?.admin_manual ?? '—'} color="#a78bfa" />
        <StatCard label="Migradas" value={stats?.by_source?.migrated ?? '—'} color="#4ade80" />
        <StatCard label="Generadas LLM" value={stats?.by_source?.llm_generated ?? '—'} color="#f472b6" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button onClick={() => setTab('recipes')} data-testid="tab-library-recipes"
          className={`px-4 py-2 rounded-t-lg text-sm font-semibold ${tab === 'recipes' ? 'bg-pink-600 text-white' : 'text-white/60 hover:text-white'}`}>
          Recetas ({total})
        </button>
        <button onClick={() => setTab('settings')} data-testid="tab-library-settings"
          className={`px-4 py-2 rounded-t-lg text-sm font-semibold ${tab === 'settings' ? 'bg-pink-600 text-white' : 'text-white/60 hover:text-white'}`}>
          Ajustes
        </button>
      </div>

      {tab === 'recipes' && (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <Input placeholder="Buscar por nombre..." value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (setPage(0), fetchAll())} className="max-w-xs" data-testid="library-search-input" />
            <select value={filterSlot} onChange={(e) => setFilterSlot(e.target.value)}
              className="bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm"
              data-testid="library-filter-slot">
              <option value="">Todos los momentos</option>
              {MEAL_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterDietType} onChange={(e) => setFilterDietType(e.target.value)}
              className="bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm"
              data-testid="library-filter-diet-type"
              title="Filtrá por cualquier categoría; incluye recetas que la tengan como principal o como etiqueta múltiple.">
              <option value="">Todas las dietas</option>
              {DIET_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(filterDietType || filterSlot) && (
              <div className="text-sm font-semibold text-fuchsia-300 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-md px-3 py-1.5"
                data-testid="library-filter-count">
                📊 {total} receta{total === 1 ? '' : 's'} encontrada{total === 1 ? '' : 's'}
              </div>
            )}
            <Button onClick={fetchAll} variant="outline" size="sm">🔎 Buscar</Button>
            <Button onClick={runAutoTag} disabled={autoTagging} data-testid="btn-auto-tag" variant="outline" size="sm"
              className="border-fuchsia-500 text-fuchsia-300 hover:bg-fuchsia-500/10">
              {autoTagging ? '⏳ Etiquetando...' : '🏷️ Auto-etiquetar'}
            </Button>
            <Button
              onClick={async () => {
                if (!window.confirm('Se detectarán recetas duplicadas por nombre (ignora mayúsculas y tildes) y se borrarán las vacías, conservando siempre la que tenga foto. ¿Continuar?')) return;
                try {
                  const r = await axios.post(`${API}/admin/recipe-library/dedupe`);
                  toast.success(`✅ Dedup completo · ${r.data.duplicate_groups} grupos · ${r.data.deleted} recetas borradas · quedan ${r.data.total_after}`, { duration: 8000 });
                  fetchAll();
                } catch (e) {
                  toast.error('Error dedup: ' + (e.response?.data?.detail || e.message));
                }
              }}
              data-testid="btn-dedupe-library"
              variant="outline"
              size="sm"
              className="border-red-500 text-red-300 hover:bg-red-500/10"
            >
              🗑️ Eliminar duplicados
            </Button>
            <Button
              onClick={async () => {
                if (!window.confirm('⚠️ REVERTIR MIGRACIÓN 13-AGO: se hará BACKUP de toda la colección y luego se conservarán solo las recetas MÁS ANTIGUAS (originales con fotos y tags), borrando las duplicadas creadas por la migración. ¿Continuar?')) return;
                try {
                  const r = await axios.post(`${API}/admin/recipe-library/dedupe-keep-oldest`);
                  toast.success(`✅ Migración revertida · Backup en ${r.data.backup_collection} · ${r.data.duplicate_groups} grupos · ${r.data.deleted} recetas borradas · quedan ${r.data.total_after}`, { duration: 10000 });
                  fetchAll();
                } catch (e) {
                  toast.error('Error: ' + (e.response?.data?.detail || e.message));
                }
              }}
              data-testid="btn-dedupe-keep-oldest"
              variant="outline"
              size="sm"
              className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
            >
              ⏮️ Revertir migración
            </Button>
            <Button
              onClick={handleNormalizeMealSlots}
              disabled={normalizingSlots}
              data-testid="btn-normalize-meal-slots"
              variant="outline"
              size="sm"
              className="border-emerald-500 text-emerald-300 hover:bg-emerald-500/10"
              title="Reescribe todos los meal_slot al valor canónico (Desayuno, Snack AM, Almuerzo, Snack PM, Cena, Post-Entreno). Idempotente."
            >
              {normalizingSlots ? '⏳ Normalizando...' : '🧹 Normalizar meal_slot'}
            </Button>
            <div className="flex-1" />
            <Button onClick={handleMigrate} disabled={migrating} data-testid="btn-migrate-recipes"
              variant="outline" size="sm" className="border-cyan-500 text-cyan-400">
              {migrating ? '⏳ Migrando...' : '📥 Migrar ya generadas'}
            </Button>
            <Button onClick={downloadTemplate} variant="outline" size="sm" data-testid="btn-download-template" className="text-white/80">
              📄 Plantilla CSV
            </Button>
            <label className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border cursor-pointer ${importing ? 'opacity-60' : ''}`}
              style={{ borderColor: 'rgba(250, 204, 21, 0.6)', color: '#facc15' }} data-testid="label-import-csv">
              {importing ? '⏳ Importando...' : '📤 Importar CSV/Excel'}
              <input type="file" accept=".csv,.xlsx,.xls,text/csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); e.target.value = ''; }}
                data-testid="input-import-file"
                disabled={importing} />
            </label>
            <Button onClick={() => setLlmDialogOpen(true)} variant="outline" size="sm"
              data-testid="btn-generate-llm" className="border-fuchsia-500 text-fuchsia-300">
              🎨 Generar con IA
            </Button>
            <Button onClick={handleAutoClassify} disabled={classifying} variant="outline" size="sm"
              data-testid="btn-auto-classify" className="border-cyan-500 text-cyan-300"
              title="Reclasifica automáticamente las recetas 'almuerzo' por su nombre (avena→desayuno, batido→snack, etc.)">
              {classifying ? '⏳ Analizando...' : '🏷️ Auto-clasificar'}
            </Button>
            <Button
              onClick={handleRepopulateImages}
              disabled={repopulatingImages}
              variant="outline"
              size="sm"
              data-testid="btn-repopulate-images"
              className="border-amber-500 text-amber-200"
              title="Re-sube a Cloudinary las imágenes que quedaron sólo con base64 y guarda el image_url resultante."
            >
              {repopulatingImages ? '⏳ Re-subiendo...' : '🖼️ Repoblar imágenes'}
            </Button>
            <Button
              onClick={loadIncompleteRecipes}
              disabled={incompleteLoading}
              variant="outline"
              size="sm"
              data-testid="btn-delete-incomplete-recipes"
              className="border-red-500 text-red-300 hover:bg-red-500/10"
              title="Encuentra recetas con ingredientes sin gramaje o con macros en 0/null, y permite eliminarlas en bloque."
            >
              {incompleteLoading ? '⏳ Buscando...' : '🧹 Eliminar recetas incompletas'}
            </Button>
            <Button onClick={() => openEdit(null)} className="bg-gradient-to-r from-pink-600 to-fuchsia-600 text-white" data-testid="btn-new-recipe">
              + Nueva Receta
            </Button>
          </div>

          {/* Resumen de la última normalización de meal_slot */}
          {normalizeResult && (
            <div
              className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-2"
              data-testid="normalize-meal-slots-summary"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-300 font-semibold">
                  🧹 Normalización de meal_slot completada
                </div>
                <button
                  onClick={() => setNormalizeResult(null)}
                  className="text-xs text-white/50 hover:text-white/80"
                  data-testid="btn-close-normalize-summary"
                >
                  ✕ Cerrar
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="rounded bg-black/30 border border-white/10 px-3 py-2">
                  <div className="text-white/60 text-xs">Corregidos</div>
                  <div className="text-emerald-300 font-bold text-lg" data-testid="normalize-updated">
                    {normalizeResult.updated ?? 0}
                  </div>
                </div>
                <div className="rounded bg-black/30 border border-white/10 px-3 py-2">
                  <div className="text-white/60 text-xs">Sin cambios</div>
                  <div className="text-white font-bold text-lg" data-testid="normalize-unchanged">
                    {normalizeResult.unchanged ?? 0}
                  </div>
                </div>
                <div className="rounded bg-black/30 border border-white/10 px-3 py-2">
                  <div className="text-white/60 text-xs">Valores canónicos</div>
                  <div className="text-white font-bold text-lg">
                    {Object.keys(normalizeResult.counts_after || {}).length}
                  </div>
                </div>
                <div className="rounded bg-black/30 border border-white/10 px-3 py-2">
                  <div className="text-white/60 text-xs">Sin mapear</div>
                  <div className={`font-bold text-lg ${(normalizeResult.unmapped_examples || []).length > 0 ? 'text-amber-300' : 'text-white'}`}>
                    {(normalizeResult.unmapped_examples || []).length}
                  </div>
                </div>
              </div>

              {normalizeResult.counts_after && (
                <div className="pt-2">
                  <div className="text-xs uppercase tracking-wider text-white/50 mb-1">
                    Conteos por meal_slot canónico (después)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(normalizeResult.counts_after).map(([slot, count]) => (
                      <div
                        key={slot}
                        className="px-2 py-1 rounded bg-white/5 border border-white/10 text-xs"
                        data-testid={`normalize-count-${slot.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <span className="text-white/80">{slot}</span>{' '}
                        <span className="text-emerald-300 font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {normalizeResult.counts_before && Object.keys(normalizeResult.counts_before).length > 0 && (
                <details className="text-xs text-white/70">
                  <summary className="cursor-pointer hover:text-white">📊 Ver conteos previos (antes de normalizar)</summary>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
                    {Object.entries(normalizeResult.counts_before).map(([slot, count]) => (
                      <div key={slot ?? 'null'} className="px-2 py-1 rounded bg-white/5">
                        <span className="text-white/60">{slot ?? '(vacío)'}</span>: <span className="text-white/90">{count}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {(normalizeResult.unmapped_examples || []).length > 0 && (
                <details className="text-xs text-amber-300 mt-1" open>
                  <summary className="cursor-pointer">⚠️ Recetas con meal_slot que no se pudo mapear (revisá manualmente)</summary>
                  <ul className="mt-2 space-y-1 list-disc pl-5">
                    {normalizeResult.unmapped_examples.map((u, i) => (
                      <li key={i}>
                        <span className="text-white/70">_id:</span> {u._id}{' — '}
                        <span className="text-white/70">meal_slot:</span> <code>{JSON.stringify(u.meal_slot)}</code>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Fila de filtros de calidad */}
          <div className="flex flex-wrap items-center gap-2 py-2 px-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)' }}>
            <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Filtros rápidos:</span>
            <FilterChip active={filterNoImage} onClick={() => setFilterNoImage(v => !v)} testId="filter-no-image" color="#f472b6">📷 Sin Foto</FilterChip>
            <FilterChip active={filterGlutenFree} onClick={() => setFilterGlutenFree(v => !v)} testId="filter-gluten-free" color="#fbbf24">🌾 Sin Gluten</FilterChip>
            <FilterChip active={filterDairyFree} onClick={() => setFilterDairyFree(v => !v)} testId="filter-dairy-free" color="#22d3ee">🥛 Sin Lactosa</FilterChip>
            <span className="text-[10px] text-white/40 ml-2">Excluir alergias:</span>
            {['Frutos Secos', 'Mariscos', 'Pescado', 'Huevo', 'Soya'].map(a => {
              const slug = a.toLowerCase().replace(/ /g, '_').replace('á', 'a').replace('é', 'e').replace('í', 'i').replace('ó', 'o').replace('ú', 'u');
              const active = filterAllergens.includes(slug);
              return (
                <FilterChip
                  key={slug}
                  active={active}
                  onClick={() => setFilterAllergens(prev => active ? prev.filter(x => x !== slug) : [...prev, slug])}
                  testId={`filter-allergen-${slug}`}
                  color="#f87171"
                >
                  🚫 {a}
                </FilterChip>
              );
            })}
            {(filterNoImage || filterGlutenFree || filterDairyFree || filterAllergens.length > 0) && (
              <button
                onClick={() => { setFilterNoImage(false); setFilterGlutenFree(false); setFilterDairyFree(false); setFilterAllergens([]); }}
                className="text-[11px] text-white/50 hover:text-white ml-auto"
                data-testid="btn-clear-filters"
              >
                Limpiar filtros
              </button>
            )}
          </div>

          {/* Grid recetas */}
          {loading ? (
            <p className="text-center text-white/60 py-6">Cargando...</p>
          ) : items.length === 0 ? (
            <p className="text-center text-white/60 py-8">
              Sin recetas todavía. Usa &quot;Migrar recetas ya generadas&quot; o &quot;Nueva Receta&quot; para empezar.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map(r => {
                const thumb = r.image_url || '';
                return (
                <Card key={r.id} className="bg-black/40 border-white/10">
                  <CardContent className="p-3">
                    <div className="flex items-start gap-2">
                      <div className="relative w-20 h-20 rounded-lg flex-shrink-0 bg-white/5 overflow-hidden flex items-center justify-center"
                        style={thumb ? { background: `url(${thumb}) center/cover` } : {}}>
                        {!thumb && <span className="text-2xl">{r.has_image ? '🖼️' : '📷'}</span>}
                        {/* Botón Subida Rápida sobre la miniatura */}
                        <label
                          className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                          style={{ background: 'rgba(0,0,0,0.65)' }}
                          title="Subida rápida de imagen"
                          data-testid={`quick-upload-${r.id}`}
                        >
                          <span className="text-xs font-bold text-white">📤 Subir</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) quickUploadImage(r, f); e.target.value = ''; }}
                          />
                        </label>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white text-sm truncate">{r.name}</p>
                        <div className="flex gap-1 flex-wrap mt-1">
                          <Badge className="text-[10px] bg-cyan-500/20 text-cyan-300">{r.meal_slot}</Badge>
                          <Badge className="text-[10px] bg-fuchsia-500/20 text-fuchsia-300" title={(r.diet_types || [r.diet_type]).join(' · ')}>
                            {(() => {
                              const arr = Array.isArray(r.diet_types) && r.diet_types.length > 0 ? r.diet_types : [r.diet_type].filter(Boolean);
                              if (arr.length <= 1) return arr[0] || '—';
                              return `${arr[0]} +${arr.length - 1}`;
                            })()}
                          </Badge>
                          {r.is_gluten_free && <Badge className="text-[10px] bg-amber-500/20 text-amber-300">🌾 Sin gluten</Badge>}
                          {r.is_dairy_free && <Badge className="text-[10px] bg-sky-500/20 text-sky-300">🥛 Sin lactosa</Badge>}
                          {(r.tags || []).slice(0, 2).map(t => (
                            <Badge key={t} className="text-[10px] bg-rose-500/15 text-rose-300 border border-rose-500/30">🚫 {t}</Badge>
                          ))}
                          {r.source === 'migrated' && <Badge className="text-[10px] bg-green-500/20 text-green-300">migrada</Badge>}
                          {r.source === 'llm_generated' && <Badge className="text-[10px] bg-yellow-500/20 text-yellow-300">LLM</Badge>}
                        </div>
                        <p className="text-xs text-white/60 mt-1">
                          {r.calories}kcal · P{r.protein} C{r.carbs} G{r.fats}
                        </p>
                        <p className="text-[10px] text-white/40 mt-0.5">Usada {r.usage_count || 0} veces</p>
                      </div>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="outline" onClick={() => openPreview(r)}
                        data-testid={`btn-preview-recipe-${r.id}`} className="flex-1 text-xs text-cyan-300 border-cyan-500/40">
                        👁 Preview
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)}
                        data-testid={`btn-edit-recipe-${r.id}`} className="flex-1 text-xs">
                        Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)}
                        data-testid={`btn-delete-recipe-${r.id}`} className="text-red-400 border-red-500/40">
                        Borrar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}

          {/* Controles de paginación */}
          {!loading && total > 0 && (
            <div className="flex items-center justify-between gap-3 mt-4 p-3 rounded-lg bg-black/30 border border-white/10"
              data-testid="recipe-library-pagination">
              <p className="text-sm text-white/70">
                Mostrando <span className="font-bold text-white">{page * PAGE_SIZE + 1}</span>
                {' – '}
                <span className="font-bold text-white">{Math.min((page + 1) * PAGE_SIZE, total)}</span>
                {' de '}
                <span className="font-bold text-white">{total}</span> recetas
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="btn-recipe-prev-page"
                  className="text-xs">
                  ← Anterior
                </Button>
                <span className="text-xs text-white/60 px-2" data-testid="recipe-page-indicator">
                  Página {page + 1} de {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </span>
                <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  data-testid="btn-recipe-next-page"
                  className="text-xs">
                  Siguiente →
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'settings' && settings && (
        <Card className="bg-black/40 border-white/10">
          <CardHeader><CardTitle className="text-white">Ajustes de la biblioteca</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <SettingRow
              label="Permitir generación con IA (fallback)"
              hint="Cuando la biblioteca no tenga suficientes recetas, generar 1 con IA y guardarla."
              checked={settings.allow_llm_fallback}
              onChange={(v) => handleUpdateSettings('allow_llm_fallback', v)}
              testid="setting-allow-llm-fallback"
            />
            <SettingRow
              label="Permitir a clientes regenerar recetas"
              hint="Muestra el botón '🔄 Regenerar' en el plan de la clienta. Usa la biblioteca (0 costo LLM)."
              checked={settings.allow_user_regenerate}
              onChange={(v) => handleUpdateSettings('allow_user_regenerate', v)}
              testid="setting-allow-user-regenerate"
            />
            <SettingRow
              label="Guardar automáticamente las generadas con IA"
              hint="Cada receta nueva que la IA genere se agrega a la biblioteca para reuso."
              checked={settings.auto_migrate_generated}
              onChange={(v) => handleUpdateSettings('auto_migrate_generated', v)}
              testid="setting-auto-migrate"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80">Tolerancia de calorías (0-1)</Label>
                <Input type="number" step="0.05" min="0" max="1" value={settings.match_calorie_tolerance}
                  onChange={(e) => handleUpdateSettings('match_calorie_tolerance', parseFloat(e.target.value) || 0.3)} />
                <p className="text-[10px] text-white/40 mt-1">±{Math.round((settings.match_calorie_tolerance || 0.3) * 100)}% del target del cliente</p>
              </div>
              <div>
                <Label className="text-white/80">Mínimo recetas por momento</Label>
                <Input type="number" min="1" max="20" value={settings.min_recipes_per_slot}
                  onChange={(e) => handleUpdateSettings('min_recipes_per_slot', parseInt(e.target.value) || 6)} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal editar receta */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-gray-900 border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Editar Receta' : 'Nueva Receta'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Nombre *</Label>
                  <Input value={editing.name || ''} onChange={(e) => updateEditingField('name', e.target.value)} data-testid="recipe-input-name" />
                </div>
                <div>
                  <Label>Momento del día</Label>
                  <select value={editing.meal_slot} onChange={(e) => updateEditingField('meal_slot', e.target.value)}
                    className="w-full bg-black/40 border border-white/15 rounded-md px-3 py-2 text-white text-sm">
                    {MEAL_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label>Tipo de dieta <span className="text-xs text-gray-400 font-normal">(múltiple — marca todas las que apliquen)</span></Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="edit-diet-types">
                    {DIET_TYPES.map((s) => {
                      const current = Array.isArray(editing.diet_types) && editing.diet_types.length > 0
                        ? editing.diet_types
                        : (editing.diet_type ? [editing.diet_type] : ['Equilibrada']);
                      const checked = current.includes(s);
                      return (
                        <label key={s}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition-colors ${
                            checked ? 'bg-fuchsia-500/20 border-fuchsia-500 text-white' : 'bg-black/30 border-white/15 text-white/80 hover:bg-white/5'
                          }`}
                          data-testid={`edit-diet-type-${s}`}>
                          <input
                            type="checkbox"
                            className="accent-fuchsia-500"
                            checked={checked}
                            onChange={(e) => {
                              let next = [...current];
                              if (e.target.checked) {
                                if (!next.includes(s)) next.push(s);
                              } else {
                                next = next.filter((x) => x !== s);
                              }
                              // Nunca dejar sin ninguna categoría: fallback a 'Equilibrada'.
                              if (next.length === 0) next = ['Equilibrada'];
                              updateEditingField('diet_types', next);
                              // Mantener `diet_type` (principal) sincronizado con la primera del array
                              updateEditingField('diet_type', next[0]);
                            }}
                          />
                          {s}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label>Preparación (min)</Label>
                  <Input type="number" value={editing.prep_minutes || 15} onChange={(e) => updateEditingField('prep_minutes', parseInt(e.target.value) || 15)} />
                </div>
                <div className="md:col-span-2">
                  <Label>📷 Foto de la receta</Label>
                  <div className="flex gap-2 items-start mt-1">
                    {/* Preview */}
                    <div className="w-24 h-24 rounded-lg flex-shrink-0 bg-white/5 overflow-hidden flex items-center justify-center"
                      style={(editing.image_url || editing.image_base64)
                        ? { background: `url(${editing.image_url || editing.image_base64}) center/cover` }
                        : {}}>
                      {!(editing.image_url || editing.image_base64) && <span className="text-3xl">🍽️</span>}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div>
                        <Input value={editing.image_url || ''}
                          onChange={(e) => updateEditingField('image_url', e.target.value)}
                          placeholder="URL de imagen (opcional)" data-testid="recipe-image-url" />
                      </div>
                      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border cursor-pointer text-white/90 hover:bg-white/5"
                        style={{ borderColor: 'rgba(255,255,255,0.2)' }} data-testid="label-upload-photo">
                        {uploadingImage ? '⏳ Subiendo...' : '📤 Subir desde mi computador'}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadImage(f); e.target.value = ''; }}
                          data-testid="input-upload-photo"
                          disabled={uploadingImage} />
                      </label>
                      {editing.image_base64 && (
                        <button type="button" onClick={() => updateEditingField('image_base64', '')}
                          className="ml-2 text-xs text-red-300 hover:underline">✕ quitar foto subida</button>
                      )}
                      <p className="text-[10px] text-white/40">Máx 3MB. Se guarda permanente en la biblioteca.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Calorías</Label>
                  <Input type="number" value={editing.calories || 0} onChange={(e) => updateEditingField('calories', parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <Label>Proteína (g)</Label>
                  <Input type="number" value={editing.protein || 0} onChange={(e) => updateEditingField('protein', parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <Label>Carbos (g)</Label>
                  <Input type="number" value={editing.carbs || 0} onChange={(e) => updateEditingField('carbs', parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <Label>Grasas (g)</Label>
                  <Input type="number" value={editing.fats || 0} onChange={(e) => updateEditingField('fats', parseFloat(e.target.value) || 0)} />
                </div>
              </div>

              <div>
                <Label>Ingredientes</Label>
                {(editing.ingredients || []).map((ing, i) => (
                  <div key={i} className="flex gap-2 mb-1">
                    <Input placeholder="Nombre" value={ing.name} onChange={(e) => updateIngredient(i, 'name', e.target.value)} className="flex-1" />
                    <Input placeholder="Cant." value={ing.quantity} onChange={(e) => updateIngredient(i, 'quantity', e.target.value)} className="w-24" />
                    <Input placeholder="Unidad" value={ing.unit || 'g'} onChange={(e) => updateIngredient(i, 'unit', e.target.value)} className="w-20" />
                    <Button size="sm" variant="ghost" onClick={() => removeIngredient(i)} className="text-red-400">X</Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addIngredient}>+ Añadir</Button>
              </div>

              <div>
                <Label>Pasos de preparación</Label>
                {(editing.instructions || []).map((s, i) => (
                  <div key={i} className="flex gap-2 mb-1">
                    <Textarea rows={2} value={s} onChange={(e) => updateInstruction(i, e.target.value)} placeholder={`Paso ${i + 1}`} className="flex-1" />
                    <Button size="sm" variant="ghost" onClick={() => removeInstruction(i)} className="text-red-400">X</Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addInstruction}>+ Añadir paso</Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Alergenos (coma)</Label>
                  <Input value={editing.allergens || ''} onChange={(e) => updateEditingField('allergens', e.target.value)} placeholder="gluten, lactosa..." />
                </div>
                <div>
                  <Label>Tags (coma)</Label>
                  <Input value={editing.tags || ''} onChange={(e) => updateEditingField('tags', e.target.value)} placeholder="alto_proteico, sin_azucar..." />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={editing.uses_protein_powder} onCheckedChange={(v) => updateEditingField('uses_protein_powder', v)} />
                <span className="text-sm text-white/80">Usa proteína en polvo</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.active !== false} onCheckedChange={(v) => updateEditingField('active', v)} />
                <span className="text-sm text-white/80">Activa (visible para clientes)</span>
              </div>

              <div className="flex gap-2 pt-3">
                <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancelar</Button>
                <Button onClick={handleSaveRecipe} className="flex-1 bg-gradient-to-r from-pink-600 to-fuchsia-600" data-testid="btn-save-recipe">
                  {editing.id ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Modal Vista Previa (cómo lo verá la clienta) */}
      <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewing(null)}>
        <DialogContent className="bg-gray-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recipe-preview-modal">
          <DialogHeader>
            <DialogTitle>Vista Previa — así la verá tu clienta</DialogTitle>
          </DialogHeader>
          {previewing && (
            <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(26,26,26,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="h-56 relative"
                style={{ background: (previewing.image_url || previewing.image_base64)
                  ? `url(${previewing.image_url || (previewing.image_base64?.startsWith('data:') ? previewing.image_base64 : `data:image/jpeg;base64,${previewing.image_base64}`)}) center/cover`
                  : 'linear-gradient(135deg, rgba(var(--brand-primary-glow),0.2), rgba(0,240,255,0.1))' }}>
                {!(previewing.image_url || previewing.image_base64) && (
                  <div className="absolute inset-0 flex items-center justify-center text-5xl">🍽️</div>
                )}
                {previewing.uses_protein_powder && (
                  <span className="absolute bottom-3 left-3 text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: 'rgba(0,240,255,0.85)', color: '#001217' }}>
                    Proteína en polvo
                  </span>
                )}
              </div>
              <div className="p-5">
                <h3 className="text-white font-black text-2xl mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{previewing.name}</h3>
                <div className="flex items-center gap-3 text-xs text-white/70 mb-4">
                  <span>⏱ {previewing.prep_minutes} min</span>
                  <span>🔥 {previewing.calories} kcal</span>
                  <span className="uppercase tracking-wider text-[10px]">{previewing.meal_slot}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[{ l: 'Proteína', v: previewing.protein, c: 'var(--brand-primary)' }, { l: 'Carbos', v: previewing.carbs, c: '#00F0FF' }, { l: 'Grasas', v: previewing.fats, c: '#FDE047' }].map((m, mi) => (
                    <div key={mi} className="text-center rounded-md py-2" style={{ background: 'rgba(0,0,0,0.4)' }}>
                      <p className="text-[10px] text-white/60 uppercase">{m.l}</p>
                      <p className="text-lg font-bold" style={{ color: m.c }}>{m.v}g</p>
                    </div>
                  ))}
                </div>
                {previewing.ingredients?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-widest text-pink-300 font-bold mb-2">Ingredientes</p>
                    <ul className="text-sm text-white/85 space-y-1">
                      {previewing.ingredients.map((ing, i) => {
                        const grams = ing.amount || (ing.quantity ? `${ing.quantity}${ing.unit || 'g'}` : '');
                        return (
                          <li key={i} data-testid={`preview-ingredient-${i}`}>
                            • {grams ? `${grams} de ` : ''}{ing.name}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {previewing.substitutions?.length > 0 && (
                  <div className="mb-4 rounded-xl p-3" style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.35)' }} data-testid="preview-substitutions">
                    <p className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-2">💡 Sustituciones sugeridas</p>
                    <ul className="text-sm text-amber-100/90 space-y-1.5">
                      {previewing.substitutions.map((s, i) => (
                        <li key={i} className="flex gap-2 items-start" data-testid={`preview-substitution-${i}`}>
                          <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5" style={{ background: 'rgba(245, 158, 11, 0.2)', color: 'rgb(252, 211, 77)' }}>{s.allergen}</span>
                          <span>{s.suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {previewing.instructions?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest text-cyan-300 font-bold mb-2">Preparación</p>
                    <ol className="text-sm text-white/85 space-y-2 list-decimal ml-5">
                      {previewing.instructions.filter(s => s.trim()).map((s, i) => (<li key={i}>{s}</li>))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Generar con IA */}
      <Dialog open={llmDialogOpen} onOpenChange={(o) => !o && setLlmDialogOpen(false)}>
        <DialogContent className="bg-gray-900 border-white/10 text-white max-w-md" data-testid="modal-generate-llm">
          <DialogHeader>
            <DialogTitle>🎨 Generar recetas con IA</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-yellow-300">
              ⚠️ Este proceso consume créditos LLM. Las recetas generadas se guardan permanentemente
              en la biblioteca para reuso futuro sin costo.
            </p>
            <div>
              <Label>Momento del día</Label>
              <select value={llmForm.meal_slot} onChange={(e) => setLlmForm({ ...llmForm, meal_slot: e.target.value })}
                className="w-full bg-black/40 border border-white/15 rounded-md px-3 py-2 text-white text-sm">
                {MEAL_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Tipo de dieta</Label>
              <select value={llmForm.diet_type} onChange={(e) => setLlmForm({ ...llmForm, diet_type: e.target.value })}
                className="w-full bg-black/40 border border-white/15 rounded-md px-3 py-2 text-white text-sm">
                {DIET_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Calorías objetivo</Label>
                <Input type="number" value={llmForm.target_calories}
                  onChange={(e) => setLlmForm({ ...llmForm, target_calories: parseInt(e.target.value) || 400 })} />
              </div>
              <div>
                <Label>Cuántas (1-6)</Label>
                <Input type="number" min="1" max="6" value={llmForm.count}
                  onChange={(e) => setLlmForm({ ...llmForm, count: parseInt(e.target.value) || 3 })} />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setLlmDialogOpen(false)} className="flex-1">Cancelar</Button>
              <Button onClick={handleGenerateLLM} disabled={generatingLLM}
                data-testid="btn-confirm-generate-llm"
                className="flex-1 bg-gradient-to-r from-fuchsia-600 to-pink-600">
                {generatingLLM ? '⏳ Generando...' : 'Generar y guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal · Recetas incompletas (2026-02) */}
      <Dialog open={incompleteOpen} onOpenChange={setIncompleteOpen}>
        <DialogContent className="max-w-3xl bg-zinc-950 border-white/10 text-white max-h-[90vh] overflow-hidden flex flex-col" data-testid="incomplete-recipes-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              🧹 Recetas incompletas
              <Badge className="bg-red-500/20 text-red-300 border border-red-500/40">{incompleteItems.length}</Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="text-xs text-white/60 -mt-2 mb-2">
            Recetas con ingredientes <strong>sin gramaje</strong> (campo <code>amount</code> vacío/null/ausente) o con alguno de los <strong>macros en 0/null</strong> (proteína, carbs o grasas).
          </div>

          {incompleteItems.length > 0 && (
            <div className="flex items-center justify-between gap-2 py-2 border-y border-white/10 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleIncompleteAll}
                data-testid="btn-select-all-incomplete"
                className="border-cyan-500 text-cyan-300 hover:bg-cyan-500/10"
              >
                {incompleteSelected.size === incompleteItems.length ? '◻️ Deseleccionar todas' : '☑️ Seleccionar todas'}
              </Button>
              <span className="text-sm text-white/70" data-testid="incomplete-selected-count">
                {incompleteSelected.size} / {incompleteItems.length} seleccionada{incompleteSelected.size === 1 ? '' : 's'}
              </span>
            </div>
          )}

          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1" data-testid="incomplete-recipes-list">
            {incompleteItems.length === 0 ? (
              <div className="py-10 text-center text-white/50 text-sm">
                ✅ No hay recetas incompletas en la biblioteca.
              </div>
            ) : (
              incompleteItems.map((r) => {
                const checked = incompleteSelected.has(r.id);
                return (
                  <label
                    key={r.id}
                    className={`flex items-start gap-3 p-2.5 rounded border cursor-pointer transition ${
                      checked
                        ? 'bg-red-500/10 border-red-500/50'
                        : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06]'
                    }`}
                    data-testid={`incomplete-recipe-row-${r.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleIncompleteOne(r.id)}
                      className="mt-1 w-4 h-4 accent-red-500"
                      data-testid={`incomplete-recipe-checkbox-${r.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white text-sm truncate">{r.name}</span>
                        {r.meal_slot && (
                          <Badge className="bg-white/10 text-white/70 border border-white/15 text-[10px] uppercase tracking-wide">
                            {r.meal_slot}
                          </Badge>
                        )}
                        {r.has_image && (
                          <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 text-[10px]">📸 con foto</Badge>
                        )}
                      </div>
                      <ul className="mt-1 text-xs text-red-300/90 list-disc pl-5 space-y-0.5">
                        {(r.reasons || []).map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  </label>
                );
              })
            )}
          </div>

          <div className="flex gap-2 pt-3 border-t border-white/10 mt-2">
            <Button
              variant="outline"
              onClick={() => setIncompleteOpen(false)}
              className="flex-1"
              data-testid="btn-cancel-incomplete"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDeleteIncomplete}
              disabled={incompleteDeleting || incompleteSelected.size === 0}
              data-testid="btn-confirm-delete-incomplete"
              className="flex-1 bg-gradient-to-r from-red-600 to-rose-600 text-white disabled:opacity-50"
            >
              {incompleteDeleting
                ? '⏳ Eliminando...'
                : `🗑️ Eliminar ${incompleteSelected.size || ''} seleccionada${incompleteSelected.size === 1 ? '' : 's'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${color}33` }}>
      <p className="text-[11px] text-white/60 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-black" style={{ color }}>{value}</p>
    </div>
  );
}

function FilterChip({ children, active, onClick, testId, color = '#22d3ee' }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all"
      style={active
        ? { background: `${color}25`, border: `1px solid ${color}`, color }
        : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)' }
      }
    >
      {children}
    </button>
  );
}

function SettingRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5">
      <div className="flex-1">
        <p className="font-semibold text-white text-sm">{label}</p>
        <p className="text-xs text-white/60">{hint}</p>
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
